import { Tag, Smartphone, Route, Brain, BarChart3, Wifi, Zap, Shield } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

const TechnologyPage = () => {
  const { t } = useLanguage();

  const technologies = [
    {
      key: 'tagTech',
      icon: Tag,
      title: t('technology.tagTech.title'),
      description: t('technology.tagTech.description'),
      features: t('technology.tagTech.features') || [],
      color: 'sky'
    },
    {
      key: 'mobile',
      icon: Smartphone,
      title: t('technology.mobile.title'),
      description: t('technology.mobile.description'),
      features: t('technology.mobile.features') || [],
      color: 'emerald'
    },
    {
      key: 'routing',
      icon: Route,
      title: t('technology.routing.title'),
      description: t('technology.routing.description'),
      features: t('technology.routing.features') || [],
      color: 'amber'
    },
    {
      key: 'engine',
      icon: Brain,
      title: t('technology.engine.title'),
      description: t('technology.engine.description'),
      features: t('technology.engine.features') || [],
      color: 'violet'
    },
    {
      key: 'analytics',
      icon: BarChart3,
      title: t('technology.analytics.title'),
      description: t('technology.analytics.description'),
      features: t('technology.analytics.features') || [],
      color: 'rose'
    }
  ];

  const colorClasses = {
    sky: { bg: 'bg-[#0077FF]/5', iconBg: 'bg-[#0077FF]/10', text: 'text-[#0077FF]', border: 'border-[#0077FF]/10' },
    emerald: { bg: 'bg-emerald-50', iconBg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-100' },
    amber: { bg: 'bg-amber-50', iconBg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-100' },
    violet: { bg: 'bg-violet-50', iconBg: 'bg-violet-100', text: 'text-violet-600', border: 'border-violet-100' },
    rose: { bg: 'bg-rose-50', iconBg: 'bg-rose-100', text: 'text-rose-600', border: 'border-rose-100' }
  };

  return (
    <div className="min-h-screen" data-testid="technology-page">
      {/* Hero */}
      <section className="gradient-hero pt-32 md:pt-40 pb-20 md:pb-24">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="flex items-center justify-center">
              <img
                src="/tech_architecture.png"
                alt="Tapelligence Technology Architecture"
                className="w-full max-w-xl h-auto rounded-2xl shadow-2xl"
                data-testid="technology-hero-illustration"
              />
            </div>
            <div>
              <h1 className="text-h1 text-slate-900 mb-6" data-testid="technology-title">
                {t('technology.hero.title')}
              </h1>
              <p className="text-body-lg">
                {t('technology.hero.subtitle')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Capabilities */}
      <section className="section-padding bg-slate-900">
        <div className="container-max">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: Wifi, label: 'Engine Tag & QR Enabled', desc: 'No app required' },
              { icon: Zap, label: 'Instant Delivery', desc: 'Sub-second alerts' },
              { icon: Shield, label: 'Enterprise Security', desc: 'Data protection' },
              { icon: BarChart3, label: 'Advanced Analytics', desc: 'Actionable insights' }
            ].map((item, index) => (
              <div key={index} className="text-center p-6 bg-slate-800/50 rounded-xl border border-slate-700">
                <div className="w-12 h-12 mx-auto mb-4 bg-[#0077FF]/20 rounded-xl flex items-center justify-center">
                  <item.icon className="w-6 h-6 text-[#4DB8FF]" />
                </div>
                <p className="text-white font-semibold mb-1">{item.label}</p>
                <p className="text-[#4DB8FF] text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Sections */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="space-y-8">
            {technologies.map((tech) => {
              const colors = colorClasses[tech.color];
              return (
                <div
                  key={tech.key}
                  className={`${colors.bg} border ${colors.border} rounded-2xl p-8 md:p-10`}
                  data-testid={`tech-section-${tech.key}`}
                >
                  <div className="grid md:grid-cols-3 gap-8">
                    <div className="md:col-span-1">
                      <div className={`w-14 h-14 ${colors.iconBg} rounded-xl flex items-center justify-center mb-4`}>
                        <tech.icon className={`w-7 h-7 ${colors.text}`} />
                      </div>
                      <h3 className="text-h3 text-slate-900 mb-3">
                        {tech.title}
                      </h3>
                      <p className="text-body">
                        {tech.description}
                      </p>
                    </div>
                    <div className="md:col-span-2">
                      <div className="grid sm:grid-cols-2 gap-4">
                        {tech.features.map((feature, fIndex) => (
                          <div
                            key={fIndex}
                            className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm"
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 ${colors.iconBg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                                <span className={`text-sm font-bold ${colors.text}`}>{fIndex + 1}</span>
                              </div>
                              <span className="text-slate-700 font-medium text-sm">{feature}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default TechnologyPage;
